<template>
  <Modal
    ref="modal"
    :title="$t('modals.share_instant_insights_report_modal.title')"
  >
    <div class="q-pa-sm">
      <div class="q-pb-md text-ds-denim-9">
        <p class="q-my-none">
          {{
            $t("modals.share_instant_insights_report_modal.select_members_text")
          }}
        </p>
        <p class="q-mb-none q-mt-sm">
          <em v-if="sharedWithUsers.length">
            {{
              $tc(
                "modals.share_instant_insights_report_modal.shared_with_text",
                sharedWithUsers.length,
                {
                  count: sharedWithUsers.length,
                }
              )
            }}
          </em>
          <em v-else>
            {{
              $t("modals.share_instant_insights_report_modal.not_shared_text")
            }}
          </em>
        </p>
      </div>
      <div class="q-pb-md">
        <ShareWithUsersSelector
          v-model="localSharedWithUsers"
          :access-levels="accessLevels"
          :hide-team-options="hideTeamOptions"
          :organisation-teams="availableTeams"
          :team-name="currentTeamName"
          :users="teamUsers"
        />
      </div>
    </div>
    <template #buttons>
      <BaseButton
        :label="$t('global.cancel')"
        new-design
        @click="cancel"
      />
      <BaseButton
        color="primary"
        :label="$t('global.confirm')"
        new-design
        @click="confirm"
      />
    </template>
  </Modal>
</template>

<script>
import { compact, keyBy, uniqBy } from "lodash-es";
import { storeToRefs } from "pinia";

import { getLocaleText } from "shared/boot/i18n";
import { BaseButton } from "shared/components/base";
import Modal from "shared/components/core/modals/Modal.vue";
import ShareWithUsersSelector from "shared/components/selectors/ShareWithUsersSelector.vue";
import useFilteredTeam from "shared/composables/useFilteredTeam";
import useTeamPicker from "shared/composables/useTeamPicker";
import { truncatedListSentence } from "shared/helpers/array";
import UserAccessible from "shared/resources/UserAccessible";
import { useUserStore } from "shared/stores/user";

const TRUNCATE_OPTIONS = { truncateAfter: 1 };

export default {
  name: "ShareInstantInsightsReportModal",
  components: {
    BaseButton,
    Modal,
    ShareWithUsersSelector,
  },
  props: {
    sharedWithUsers: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["confirm", "cancel"],
  setup() {
    const userStore = useUserStore();
    const { currentUser, sortedTeam: team } = storeToRefs(userStore);
    const { getTeam } = userStore;

    const { organisationTeamFromPreference } = useTeamPicker();
    const { teamUsers } = useFilteredTeam();

    return {
      currentUser,
      team,
      getTeam,
      organisationTeamFromPreference,
      teamUsers,
    };
  },
  data() {
    return {
      localSharedWithUsers: [],
    };
  },
  computed: {
    accessLevels() {
      return {
        read_only: {
          label: getLocaleText("global.shared"),
          permissions: ["read_only"],
        },
      };
    },
    restrictContentSharingToTeam() {
      return (
        this.$features.has("has_restricted_content_sharing_to_team") &&
        !this.$permissions.has("can_share_with_all_organisation_users")
      );
    },
    availableTeams() {
      let availableTeams = [];

      if (this.hideTeamOptions) return availableTeams;

      if (this.restrictContentSharingToTeam) {
        availableTeams = this.currentUser.organisation_teams.map(
          (team) => team
        );
      } else {
        availableTeams = compact(
          uniqBy(
            this.team
              .filter(
                (user) =>
                  user.organisation_teams.filter((team) => team.primary).length
              )
              .flatMap((user) =>
                user.organisation_teams.filter((team) => team.primary)
              ),
            "id"
          )
        );
      }

      return availableTeams;
    },
    hideTeamOptions() {
      return this.$features.has("has_team_workspaces");
    },
    currentTeamName() {
      return this.organisationTeamFromPreference?.name;
    },
  },
  async mounted() {
    this.localSharedWithUsers = this.sharedWithUsers.map(
      (sharedRecordsUser) => ({
        id: sharedRecordsUser.userId,
        accessLevel: sharedRecordsUser.accessLevel,
      })
    );

    if (!this.team.length) await this.getTeam();
  },
  methods: {
    confirm() {
      const { updatedShares, updatedNames } = this.buildUpdatedShares();
      const message = this.buildStatusMessage(updatedNames);

      this.$emit("confirm", updatedShares, message);
      this.close();
    },
    cancel() {
      this.$emit("cancel");
      this.close();
    },
    buildUpdatedShares() {
      const lookup = keyBy(this.sharedWithUsers, "userId");

      const updatedShares = [];
      const sharedNames = [];
      const removedNames = [];

      this.localSharedWithUsers.forEach((user) => {
        const existingRecord = lookup[user.id];

        if (existingRecord) {
          const { updatedRecord, removed } = this.buildUpdatedUserAccess(
            existingRecord,
            user.accessLevel
          );

          updatedShares.push(updatedRecord);

          if (removed) {
            removedNames.push(user.name);
          } else if (updatedRecord.isDirty()) {
            sharedNames.push(user.name);
          }
        } else if (user.accessLevel) {
          sharedNames.push(user.name);

          updatedShares.push(
            new UserAccessible({
              userId: user.id,
              accessLevel: user.accessLevel,
            })
          );
        }
      });

      return {
        updatedShares,
        updatedNames: {
          sharedNames,
          removedNames,
        },
      };
    },
    buildUpdatedUserAccess(existingRecord, accessLevel) {
      const updatedRecord = existingRecord.dupWithDirtyTracking();
      let removed = false;

      if (!accessLevel) {
        updatedRecord.isMarkedForDestruction = true;
        removed = true;
      } else {
        updatedRecord.accessLevel = accessLevel;
      }

      return {
        updatedRecord,
        removed,
      };
    },
    buildStatusMessage({ sharedNames, removedNames }) {
      let message;

      if (sharedNames.length) {
        const names = truncatedListSentence(sharedNames, TRUNCATE_OPTIONS);

        message = this.$t(
          "modals.share_instant_insights_report_modal.build_status_shared_message",
          {
            names,
          }
        );
      } else if (removedNames.length) {
        const names = truncatedListSentence(removedNames, TRUNCATE_OPTIONS);

        message = this.$t(
          "modals.share_instant_insights_report_modal.build_status_removed_message",
          {
            names,
          }
        );
      } else {
        message = this.$t(
          "modals.share_instant_insights_report_modal.build_status_empty_message"
        );
      }

      return message;
    },
    close() {
      this.$refs.modal.close();
    },
  },
};
</script>
